import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  IconButton,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useDataProvider, useNotify, useGetIdentity } from 'react-admin';

interface ContactLog {
  id?: string;
  type: 'Call' | 'Email' | 'SMS' | 'InPerson' | 'Other';
  direction: 'Inbound' | 'Outbound';
  subject: string;
  notes?: string;
  contactedById?: number;
  contactDate: string;
}

interface ContactLogDialogProps {
  open: boolean;
  onClose: () => void;
  customerId?: string;
  leadId?: string;
  editingLog?: ContactLog | null;
}

export const ContactLogDialog: React.FC<ContactLogDialogProps> = ({
  open,
  onClose,
  customerId,
  leadId,
  editingLog: propEditingLog,
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { data: identity } = useGetIdentity();
  const [editingLog, setEditingLog] = useState<ContactLog | null>(null);
  const [users, setUsers] = useState<Array<{ id: number; name: string; email: string }>>([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [formData, setFormData] = useState<ContactLog>({
    type: 'Other',
    direction: 'Outbound',
    subject: '',
    notes: '',
    contactedById: identity?.id || undefined,
    contactDate: new Date().toLocaleDateString('en-CA'), // 'en-CA' gives YYYY-MM-DD format in local timezone
  });

  const loadUsers = useCallback(async () => {
    try {
      const response = await dataProvider.getList('users', {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'name', order: 'ASC' },
      });
      setUsers(response.data);
    } catch (error) {
      // Error loading users
    }
  }, [dataProvider]);

  useEffect(() => {
    if (open) {
      loadUsers();
      if (propEditingLog) {
        setEditingLog(propEditingLog);
        setFormData(propEditingLog);
      } else {
        setEditingLog(null);
        setFormData({
          type: 'Other',
          direction: 'Outbound',
          subject: '',
          notes: '',
          contactedById: identity?.id || undefined,
          contactDate: new Date().toLocaleDateString('en-CA'), // 'en-CA' gives YYYY-MM-DD format in local timezone
        });
      }
    }
  }, [open, propEditingLog, identity?.id, loadUsers]);

  useEffect(() => {
    if (identity?.id && !propEditingLog) {
      setFormData(prev => ({
        ...prev,
        contactedById: identity.id,
      }));
    }
  }, [identity, propEditingLog]);

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value,
      };

      // Clear contactedById when switching to Inbound
      if (field === 'direction' && value === 'Inbound') {
        newData.contactedById = undefined;
      }
      // Set contactedById to current user when switching to Outbound
      else if (field === 'direction' && value === 'Outbound' && identity?.id) {
        newData.contactedById = identity.id;
      }

      return newData;
    });
  };

  const handleDeleteConfirm = async () => {
    if (!editingLog?.id) {
      return;
    }

    try {
      await dataProvider.delete('contact-logs', { id: editingLog.id });
      notify('Contact log deleted successfully', { type: 'success' });
      setDeleteConfirmOpen(false);
      onClose();
    } catch (error) {
      notify('Error deleting contact log', { type: 'error' });
      setDeleteConfirmOpen(false);
    }
  };

  const handleSave = async () => {
    try {
      const saveData = {
        ...formData,
        ...(customerId && { customerId }),
        ...(leadId && { leadId }),
      };

      if (editingLog) {
        await dataProvider.update('contact-logs', {
          id: editingLog.id,
          data: saveData,
          previousData: editingLog,
        });
        notify('Contact log updated successfully', { type: 'success' });
      } else {
        await dataProvider.create('contact-logs', {
          data: saveData,
        });
        notify('Contact log created successfully', { type: 'success' });
      }

      // Close the dialog
      onClose();
    } catch (error) {
      notify('Error saving contact log', { type: 'error' });
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              {editingLog ? 'Edit Contact Log' : 'Add New Contact Log'}
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Contact Type"
                value={formData.type}
                onChange={handleChange('type')}
                select
                SelectProps={{
                  native: true,
                }}
                required
              >
                <option value="Call">Phone Call</option>
                <option value="Email">Email</option>
                <option value="SMS">SMS</option>
                <option value="InPerson">In Person</option>
                <option value="Other">Other</option>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Direction"
                value={formData.direction}
                onChange={handleChange('direction')}
                select
                SelectProps={{
                  native: true,
                }}
                required
              >
                <option value="Outbound">Outbound</option>
                <option value="Inbound">Inbound</option>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Contact Date"
                type="date"
                value={formData.contactDate}
                onChange={handleChange('contactDate')}
                InputLabelProps={{
                  shrink: true,
                }}
                required
              />
            </Grid>
            {formData.direction === 'Outbound' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Contacted By"
                  value={formData.contactedById || ''}
                  onChange={handleChange('contactedById')}
                  select
                  SelectProps={{
                    native: true,
                  }}
                >
                  <option value="">Select User</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name || user.email}
                    </option>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subject"
                value={formData.subject}
                onChange={handleChange('subject')}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={formData.notes}
                onChange={handleChange('notes')}
                multiline
                rows={4}
                placeholder="Add notes about this contact..."
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          {editingLog && (
            <Button onClick={() => setDeleteConfirmOpen(true)} color="error">
              Delete
            </Button>
          )}
          <Button onClick={handleSave} variant="contained">
            {editingLog ? 'Update Log' : 'Add Log'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Contact Log</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this contact log? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
